import { Fragment, useMemo, useState } from 'react'
import { Copy, KeyRound, MoreHorizontal, RotateCw, Trash2 } from 'lucide-react'
import {
  useDeleteUserSessionMutation,
  useDisableUserMutation,
  useGetUsersQuery,
  type EntitiesCreateUserSessionResponse,
  type EntitiesSessionResponse,
  type EntitiesUserResponse
} from '@axilon-apps/domains-identity/state'
import type { IngestIntake } from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../../../../components/ui/badge'
import { Button } from '../../../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../../../components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../../components/ui/table'
import { ConfirmDialog } from '../../../ConfigurePage/components/DeleteConfirmDialog'
import { formatRelativeTime } from '../../../../lib/time'
import { apiKeyErrorMessage } from './api-key-error'
import {
  API_KEY_PERMISSION,
  apiKeyHasPermission,
  apiKeyPermissionLabel,
  apiKeyPermissions
} from './api-key-permissions'
import { apiKeyExpiry, apiKeySessions, newestLastUsed } from './api-key-view'
import { IssuedTokenDialog } from './IssuedTokenDialog'
import { RotateApiKeyDialog } from './RotateApiKeyDialog'

interface ApiKeysTableProps {
  apiKeys: EntitiesUserResponse[]
  intakes: IngestIntake[]
  proxyApiV1Url: string
}

type PendingAction =
  | { kind: 'revoke'; apiKey: EntitiesUserResponse; session: EntitiesSessionResponse }
  | { kind: 'delete'; apiKey: EntitiesUserResponse }

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Unknown'
  const date = new Date(value)
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : 'Unknown'
}

function expiryLabel(sessions: EntitiesSessionResponse[]): {
  text: string
  state: ReturnType<typeof apiKeyExpiry>['state']
} {
  const expiry = apiKeyExpiry(sessions)
  if (expiry.state === 'none') return { text: 'No active tokens', state: expiry.state }
  if (expiry.state === 'unknown') return { text: 'Expiry unknown', state: expiry.state }
  if (expiry.state === 'expired') {
    return {
      text: expiry.at ? `Expired ${formatRelativeTime(expiry.at, { fallback: '' })}` : 'Expired',
      state: expiry.state
    }
  }
  return {
    text: expiry.at ? formatRelativeTime(expiry.at, { fallback: 'Unknown' }) : 'Unknown',
    state: expiry.state
  }
}

export function ApiKeysTable({ apiKeys, intakes, proxyApiV1Url }: ApiKeysTableProps) {
  const [expandedKeyId, setExpandedKeyId] = useState<string | null>(null)
  const [rotating, setRotating] = useState<EntitiesUserResponse | null>(null)
  const [issued, setIssued] = useState<{
    apiKey: EntitiesUserResponse
    token: EntitiesCreateUserSessionResponse
  } | null>(null)
  const [copying, setCopying] = useState<EntitiesUserResponse | null>(null)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  const [actionError, setActionError] = useState('')
  const [actionSubmitting, setActionSubmitting] = useState(false)
  // Revoking a token deletes its session; deleting a key disables the
  // service-account user, which revokes every session and keeps the user
  // row for attribution.
  const [revokeSession, revokeState] = useDeleteUserSessionMutation()
  const [disableApiKey, disableState] = useDisableUserMutation()
  const { data: creators } = useGetUsersQuery({
    page: 1,
    perPage: 100,
    serviceAccounts: 'exclude'
  })
  const creatorsById = useMemo(
    () =>
      new Map(
        (creators?.items ?? []).map((user) => [
          user.id,
          user.name?.trim() || user.username?.trim() || user.id
        ])
      ),
    [creators?.items]
  )

  const confirmAction = async () => {
    if (!pendingAction) return
    setActionError('')
    setActionSubmitting(true)
    try {
      if (pendingAction.kind === 'revoke') {
        await revokeSession({
          id: pendingAction.apiKey.id,
          sessionId: pendingAction.session.id
        }).unwrap()
      } else {
        await disableApiKey(pendingAction.apiKey.id).unwrap()
      }
      setPendingAction(null)
    } catch (error) {
      setActionError(apiKeyErrorMessage(error))
    } finally {
      setActionSubmitting(false)
    }
  }

  const pendingDescription =
    pendingAction?.kind === 'revoke'
      ? `Revoke the token created ${formatDate(pendingAction.session.createdAt)} for ${pendingAction.apiKey.name}? Requests using it will receive 401 on their next request.`
      : pendingAction?.kind === 'delete'
        ? `Delete ${pendingAction.apiKey.name}? Every token will be revoked, this key will disappear, and senders using it will receive 401.`
        : ''

  return (
    <>
      <Table className="table-fixed">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-8 w-[30%] px-2 text-10 uppercase tracking-wide text-ink-3">
              Name
            </TableHead>
            <TableHead className="h-8 w-[20%] px-2 text-10 uppercase tracking-wide text-ink-3">
              Permissions
            </TableHead>
            <TableHead className="h-8 w-[10%] px-2 text-center text-10 uppercase tracking-wide text-ink-3">
              Tokens
            </TableHead>
            <TableHead className="h-8 w-[19%] px-2 text-10 uppercase tracking-wide text-ink-3">
              Expires
            </TableHead>
            <TableHead className="h-8 w-[15%] px-2 text-10 uppercase tracking-wide text-ink-3">
              Last used
            </TableHead>
            <TableHead className="h-8 w-10 px-1">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {apiKeys.map((apiKey) => {
            const expanded = expandedKeyId === apiKey.id
            const sessions = apiKeySessions(apiKey)
            const expiry = expiryLabel(sessions)
            const lastUsed = newestLastUsed(sessions)
            const permissions = apiKeyPermissions(apiKey.roleAssignments)
            const canPushToIntakes = apiKeyHasPermission(permissions, API_KEY_PERMISSION.IngestPush)
            const permissionLabel =
              permissions.map(apiKeyPermissionLabel).join(', ') || 'No permissions'
            return (
              <Fragment key={apiKey.id}>
                <TableRow
                  role="button"
                  tabIndex={0}
                  aria-label={`${expanded ? 'Collapse' : 'Expand'} ${apiKey.name}`}
                  aria-expanded={expanded}
                  className={`cursor-pointer outline-none transition-colors focus-visible:bg-muted/60 ${
                    expanded ? 'bg-muted/45 hover:bg-muted/55' : 'hover:bg-muted/30'
                  }`}
                  onClick={() => setExpandedKeyId(expanded ? null : apiKey.id)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    setExpandedKeyId(expanded ? null : apiKey.id)
                  }}
                >
                  <TableCell className="min-w-0 whitespace-normal px-2 py-2.5">
                    <div className="flex min-w-0 items-start gap-2">
                      <KeyRound className="mt-0.5 size-3.5 shrink-0 text-ink-3" />
                      <div className="min-w-0">
                        <div className="truncate text-xs font-medium text-ink" title={apiKey.name}>
                          {apiKey.name}
                        </div>
                        <div className="mt-0.5 text-10 text-ink-3">
                          Created {formatDate(apiKey.createdAt)}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell
                    className="min-w-0 whitespace-normal px-2 py-2.5"
                    title={permissionLabel}
                  >
                    <div className="truncate text-xs text-ink-2">{permissionLabel}</div>
                    <div className="mt-0.5 truncate text-10 text-ink-3" title={apiKey.username}>
                      {apiKey.username}
                    </div>
                  </TableCell>
                  <TableCell className="px-2 py-2.5 text-center text-xs tabular-nums text-ink-2">
                    {sessions.length}
                    {sessions.length >= 2 ? (
                      <span
                        className="ml-1 inline-block size-1.5 rounded-full bg-st-warn align-middle"
                        title="Revoke the old token after the sender switches."
                      />
                    ) : null}
                  </TableCell>
                  <TableCell className="min-w-0 px-2 py-2.5">
                    <Badge
                      variant="outline"
                      className={`max-w-full overflow-hidden px-1.5 py-0 text-10 font-normal text-ellipsis ${
                        expiry.state === 'expired'
                          ? 'border-st-down/30 bg-st-down-bg text-st-down'
                          : expiry.state === 'expiring'
                            ? 'border-st-warn/30 bg-st-warn-bg text-st-warn'
                            : ''
                      }`}
                    >
                      {expiry.text}
                    </Badge>
                  </TableCell>
                  <TableCell className="truncate px-2 py-2.5 text-xs text-ink-2">
                    {formatRelativeTime(lastUsed, { fallback: 'Never' })}
                  </TableCell>
                  <TableCell className="px-1 py-2.5 text-right">
                    <div
                      className="inline-flex"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`More actions for ${apiKey.name}`}
                          >
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem onSelect={() => setRotating(apiKey)}>
                            <RotateCw />
                            Rotate token
                          </DropdownMenuItem>
                          {canPushToIntakes ? (
                            <DropdownMenuItem onSelect={() => setCopying(apiKey)}>
                              <Copy />
                              Copy curl
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            variant="destructive"
                            onSelect={() => {
                              setActionError('')
                              setPendingAction({ kind: 'delete', apiKey })
                            }}
                          >
                            <Trash2 />
                            Delete key
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow className="border-0 hover:bg-transparent">
                  <TableCell colSpan={6} className="p-0 whitespace-normal">
                    <div
                      aria-hidden={!expanded}
                      className={`grid overflow-hidden transition-[grid-template-rows,opacity] duration-200 ease-out ${
                        expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                      }`}
                    >
                      <div className="min-h-0 overflow-hidden">
                        <div className="border-b border-border bg-bg-sunk/40 px-3 py-2.5">
                          {sessions.length === 0 ? (
                            <p className="text-xs text-ink-3">
                              No active tokens. Rotate to issue one.
                            </p>
                          ) : (
                            <>
                              {sessions.length >= 2 ? (
                                <p className="mb-2 text-10 text-st-warn">
                                  Revoke the old token after the sender has switched.
                                </p>
                              ) : null}
                              <div className="divide-y divide-border/70 border-y border-border/70">
                                {sessions.map((session) => (
                                  <div
                                    key={session.id}
                                    className="grid grid-cols-2 items-center gap-x-4 gap-y-2 py-2 text-11 sm:grid-cols-[repeat(4,minmax(0,1fr))_auto]"
                                  >
                                    <div>
                                      <div className="text-9 uppercase tracking-wide text-ink-3">
                                        Created
                                      </div>
                                      <div className="mt-0.5 text-ink-2">
                                        {formatDate(session.createdAt)}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-9 uppercase tracking-wide text-ink-3">
                                        Expires
                                      </div>
                                      <div className="mt-0.5 text-ink-2">
                                        {formatDate(session.expiresAt)}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-9 uppercase tracking-wide text-ink-3">
                                        Last used
                                      </div>
                                      <div className="mt-0.5 text-ink-2">
                                        {formatRelativeTime(session.lastUsedAt ?? null, {
                                          fallback: 'Never'
                                        })}
                                      </div>
                                    </div>
                                    <div className="min-w-0">
                                      <div className="text-9 uppercase tracking-wide text-ink-3">
                                        Created by
                                      </div>
                                      <div
                                        className="mt-0.5 truncate text-ink-2"
                                        title={session.createdBy ?? undefined}
                                      >
                                        {session.createdBy
                                          ? (creatorsById.get(session.createdBy) ??
                                            session.createdBy)
                                          : 'Unknown'}
                                      </div>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      tabIndex={expanded ? 0 : -1}
                                      className="col-span-2 h-7 justify-self-end px-2 text-10 sm:col-span-1"
                                      onClick={() => {
                                        setActionError('')
                                        setPendingAction({ kind: 'revoke', apiKey, session })
                                      }}
                                    >
                                      Revoke
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </Fragment>
            )
          })}
        </TableBody>
      </Table>

      <RotateApiKeyDialog
        apiKey={rotating}
        onOpenChange={(open) => {
          if (!open) setRotating(null)
        }}
        onIssued={(token) => {
          if (rotating) setIssued({ apiKey: rotating, token })
          setRotating(null)
        }}
      />
      <IssuedTokenDialog
        open={issued !== null}
        keyName={issued?.apiKey.name ?? ''}
        issuedToken={issued?.token ?? null}
        intakes={intakes}
        showIntakeCommand={
          issued
            ? apiKeyHasPermission(
                apiKeyPermissions(issued.apiKey.roleAssignments),
                API_KEY_PERMISSION.IngestPush
              )
            : false
        }
        proxyApiV1Url={proxyApiV1Url}
        onClose={() => setIssued(null)}
      />
      <IssuedTokenDialog
        open={copying !== null}
        keyName={copying?.name ?? ''}
        issuedToken={null}
        intakes={intakes}
        showIntakeCommand={
          copying
            ? apiKeyHasPermission(
                apiKeyPermissions(copying.roleAssignments),
                API_KEY_PERMISSION.IngestPush
              )
            : false
        }
        proxyApiV1Url={proxyApiV1Url}
        onClose={() => setCopying(null)}
      />
      <ConfirmDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open && !actionSubmitting && !revokeState.isLoading && !disableState.isLoading) {
            setPendingAction(null)
            setActionError('')
          }
        }}
        title={pendingAction?.kind === 'revoke' ? 'Revoke token' : 'Delete API key'}
        description={actionError ? `${pendingDescription} ${actionError}` : pendingDescription}
        confirmLabel={pendingAction?.kind === 'revoke' ? 'Revoke token' : 'Delete key'}
        destructive
        keepOpenOnConfirm
        busy={actionSubmitting || revokeState.isLoading || disableState.isLoading}
        onConfirm={() => void confirmAction()}
      />
    </>
  )
}
